import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, CheckCircle } from 'lucide-react';
import { FormField } from '../identity/shared/FormField';
import { OAuthProviders, OAuthProvider } from './OAuthProviders';
import { useJWTAuth } from '../../contexts/useJWTAuth';
import { API_CONFIG } from '../../config/api.config';
import { logger } from '../../utils/logger';

interface SignInFormProps {
  onSuccess: () => void;
  onSwitchToSignUp?: () => void;
}

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface ValidationErrors {
  email?: string;
  password?: string;
}

export const SignInForm: React.FC<SignInFormProps> = ({ onSuccess }) => {
  const { signIn, isLoading, error, clearError } = useJWTAuth();

  // Form state
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    return undefined;
  };

  // Real-time validation
  const validateField = useCallback((field: keyof FormData, value: string) => {
    let error: string | undefined;

    switch (field) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
    }

    setValidationErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  }, []);

  // Handle input changes
  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear auth errors when user starts typing
    if (error) {
      clearError();
    }

    // Validate field on change (debounced for better UX)
    if (typeof value === 'string' && value.length > 0) {
      setTimeout(() => validateField(field as keyof FormData, value), 300);
    }
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
    };

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== undefined);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Pass onSuccess as the callback - it will be called after user state is set
      await signIn(formData.email.trim(), formData.password, () => {
        // Handle remember me functionality
        if (formData.rememberMe) {
          localStorage.setItem('remember_email', formData.email.trim());
        } else {
          localStorage.removeItem('remember_email');
        }

        // Navigate to dashboard - called after auth state is fully propagated
        onSuccess();
      });
    } catch (error) {
      // Error is handled by the auth context
      logger.error('Sign in failed', { error });
    }
  };

  // Handle OAuth sign in
  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setOauthLoading(provider);
    try {
      const response = await fetch(`${API_CONFIG.apiUrl}/auth/oauth/${provider}/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to initiate ${provider} OAuth`);
      }

      const { authorizationUrl } = await response.json();

      // Redirect to the provider's OAuth authorization page
      window.location.href = authorizationUrl;
    } catch (error) {
      logger.error(`${provider} sign in failed`, { error });
      alert(error instanceof Error ? error.message : `Failed to sign in with ${provider}`);
      setOauthLoading(null);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!formData.email) {
      return;
    }

    setResendingVerification(true);
    setVerificationResent(false);

    try {
      await fetch(`${API_CONFIG.apiUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email.trim() }),
      });

      // Always show success to avoid email enumeration
      setVerificationResent(true);

      // Clear the error after successful resend
      setTimeout(() => {
        clearError();
      }, 100);
    } catch (error) {
      logger.error('Failed to resend verification email', { error });
      // Still show success to avoid email enumeration
      setVerificationResent(true);
    } finally {
      setResendingVerification(false);
    }
  };

  // Load remembered email on component mount
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('remember_email');
    if (rememberedEmail) {
      setFormData((prev) => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true,
      }));
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <FormField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(value) => handleInputChange('email', value)}
          error={validationErrors.email}
          required
          placeholder="Enter your email address"
          autoComplete="email"
          disabled={isLoading}
        />

        {/* Password */}
        <div className="space-y-2">
          <FormField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(value) => handleInputChange('password', value)}
            error={validationErrors.password}
            required
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isLoading}
          />

          {/* Forgot Password Link */}
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium underline hover:no-underline transition-all duration-200"
              style={{ color: 'var(--color-accent-teal)' }}
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        {/* Remember Me */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="rememberMe"
            checked={formData.rememberMe}
            onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
            disabled={isLoading}
            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 focus:ring-offset-0"
            style={{
              accentColor: 'var(--color-accent-teal)',
            }}
          />
          <label
            htmlFor="rememberMe"
            className="text-sm cursor-pointer"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Remember me
          </label>
        </div>

        {/* Verification Email Resent Success */}
        {verificationResent && (
          <div className="p-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  Verification email sent!
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                  Check your inbox and click the verification link to continue.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setVerificationResent(false)}
              className="mt-2 text-xs text-green-600 hover:text-green-700 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Auth Error Display */}
        {error && !verificationResent && (
          <div
            className={`p-3 rounded-lg border ${
              error.code === 'EMAIL_NOT_VERIFIED'
                ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
            }`}
          >
            {error.code === 'EMAIL_NOT_VERIFIED' ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                      Email verification required
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                      Please verify your email address before signing in.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={resendingVerification}
                  onClick={handleResendVerification}
                  className="w-full py-2 px-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--color-accent-teal)',
                    color: 'white',
                    opacity: resendingVerification ? 0.7 : 1,
                  }}
                >
                  {resendingVerification ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Resend verification email
                    </>
                  )}
                </button>
              </div>
            ) : (
              <p className="text-sm text-red-600 dark:text-red-400">
                {error.code === 'INVALID_CREDENTIALS'
                  ? 'Invalid email or password. Please try again.'
                  : error.message}
              </p>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-medium"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Signing In...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* OAuth Providers */}
      <OAuthProviders
        onProvider={handleOAuthSignIn}
        isLoading={!!oauthLoading}
        loadingProvider={oauthLoading}
        disabled={isLoading}
      />

      {/* Sign Up Link */}
      <div className="text-center">
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Don't have an account?{' '}
          <Link
            to="/signup"
            className="font-medium underline hover:no-underline transition-all duration-200"
            style={{ color: 'var(--color-accent-teal)' }}
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
};
